import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Pagination,
  TextField,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import {
  websiteFormsApi,
  WorkWithUs,
  PaginatedWorkWithUsResponse,
} from '../services/websiteFormsService.ts';
import { formatPhoneNumber } from '../utils/formatPhone.ts';

const WorkWithUsPage: React.FC = () => {
  const { sessionToken } = useAuth();
  const { showError } = useSnackbar();
  const [submissions, setSubmissions] = useState<WorkWithUs[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [emailSearch, setEmailSearch] = useState('');
  const [emailSearchInput, setEmailSearchInput] = useState('');

  const fetchSubmissions = async (pageNum: number = 1, email?: string) => {
    if (!sessionToken) return;

    setLoading(true);

    try {
      const response: PaginatedWorkWithUsResponse =
        await websiteFormsApi.getWorkWithUs(
          { page: pageNum, limit, email },
          sessionToken
        );

      setSubmissions(response.data);
      setPage(response.page);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to fetch work with us submissions';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, [sessionToken]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
    fetchSubmissions(value, emailSearch || undefined);
  };

  const handleSearch = () => {
    setEmailSearch(emailSearchInput);
    setPage(1);
    fetchSubmissions(1, emailSearchInput || undefined);
  };

  const handleClearSearch = () => {
    setEmailSearchInput('');
    setEmailSearch('');
    setPage(1);
    fetchSubmissions(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  if (loading && submissions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ color: "#7f0000", fontWeight: 700, flex: "0 0 auto", whiteSpace: "nowrap" }}>
          Date: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          Work With Us Submissions
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => fetchSubmissions(page, emailSearch || undefined)}
        >
          Refresh
        </Button>
      </Box>

      {/* Search Bar */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            size="small"
            label="Search by Email"
            value={emailSearchInput}
            onChange={(e) => setEmailSearchInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter email address..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: emailSearchInput && (
                <InputAdornment position="end">
                  <Button
                    size="small"
                    onClick={handleClearSearch}
                    sx={{ minWidth: 'auto', p: 0.5 }}
                  >
                    <ClearIcon fontSize="small" />
                  </Button>
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading}
            sx={{ minWidth: '100px' }}
          >
            Search
          </Button>
        </Box>
        {emailSearch && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Showing results for: <strong>{emailSearch}</strong>
          </Typography>
        )}
      </Paper>

      <Paper sx={{ width: '100%', overflow: 'hidden', border: '2px solid #7f0000' }}>
        <TableContainer>
          <Table sx={{ borderCollapse: 'collapse', '& td': { border: '2px solid #9e9e9e', py: 1.25, px: 1.5 } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                {['Name', 'Email', 'Phone', 'Address', 'City, State ZIP', 'Current Employer', 'Position', 'Message', 'Created At'].map((label) => (
                  <TableCell
                    key={label}
                    sx={{
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      border: '2px solid #7f0000',
                      py: 1.5,
                      px: 1.5,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {emailSearch
                        ? 'No submissions found matching the search criteria'
                        : 'No submissions found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                submissions.map((submission) => (
                  <TableRow key={submission.id} hover>
                    <TableCell>{submission.name}</TableCell>
                    <TableCell>{submission.email}</TableCell>
                    <TableCell>{formatPhoneNumber(submission.phoneNumber)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {submission.address}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {submission.city}, {submission.state} {submission.zipCode}
                    </TableCell>
                    <TableCell>
                      {submission.currentEmployer || (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={submission.positionApplyingFor}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {submission.message ? (
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={submission.message}
                        >
                          {submission.message}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatDateTime(submission.createdAt)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {submissions.length} of {total} submission
              {total !== 1 ? 's' : ''}
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default WorkWithUsPage;

